import {
  FREQUENCY_LABELS,
  dayLabel,
  formatDateLong,
  invertRate,
  monthOf,
  type AccountDto,
  type Currency,
  type ScheduledDto,
  type TransactionDto,
} from '@koverta/shared';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useToast } from '../components/toast';
import { Avatar, Btn, CurrencyChip, Icon, Seg, Sheet, Spinner } from '../components/ui';
import { del, errorMessage, patch, post } from '../lib/api';
import { useClickOutside, useDebounced, useIsMobile } from '../lib/hooks';
import { useHousehold, type Fmt } from '../lib/household';
import { invalidateScopes, useAccounts, useScheduled, useTransactions } from '../lib/queries';
import { useShell } from '../shell/Shell';
import '../styles/register.css';
import '../styles/scheduled.css';

const ImportDialog = lazy(() => import('./ImportDialog').then((m) => ({ default: m.ImportDialog })));
import { TransactionDialog } from './AddTransaction';
import { EditAccountDialog, KIND_LABEL } from './EditAccountDialog';
import { LoanPanel } from './LoanPanel';
import { ReconcileDialog } from './ReconcileDialog';
import { SwitchCurrencyDialog } from './SwitchCurrencyDialog';
import {
  firstDir,
  inView,
  matchesSearch,
  parseTyped,
  sortTxs,
  type RegisterView,
  type Sort,
  type SortKey,
  type SuggestSource,
} from './register-filter';
import { RegisterSearch, ViewOptions, rangeLabel, useRegisterState } from './RegisterSearch';

type DialogKind = 'reconcile' | 'import' | 'switch' | 'edit' | null;
const otherCurrency = (c: Currency): Currency => (c === 'RSD' ? 'EUR' : 'RSD');

export function RegisterPage() {
  const { id = 'all' } = useParams();
  const [sp] = useSearchParams();
  const accounts = useAccounts();
  const mobile = useIsMobile();
  const account = accounts.data?.find((a) => a.id === id) ?? null;
  if (accounts.isPending) return <Spinner />;
  if (id !== 'all' && !account) return <div className="banner warn">Account not found.</div>;
  const props = { account, category: sp.get('category') ?? undefined, month: sp.get('month') ?? undefined };
  // Keyed so search, selection and view start fresh when you switch accounts or follow a Plan link.
  const key = `${id}|${props.category ?? ''}|${props.month ?? ''}`;
  return mobile ? <RegisterMobile key={key} {...props} /> : <RegisterDesktop key={key} {...props} />;
}

// ───────── Upcoming (scheduled-transactions.md §5) ─────────

/** A schedule as seen from one account: a transfer shown in its other account has the opposite sign. */
function seenFrom(s: ScheduledDto, accountId: string | undefined) {
  if (accountId && s.transfer?.accountId === accountId) {
    const other = s.transfer.amount ?? Math.abs(s.amount);
    return {
      amount: s.amount < 0 ? other : -other,
      currency: s.transfer.currency,
      payee: `Transfer ${s.amount < 0 ? '←' : '→'} ${s.accountName}`,
    };
  }
  return { amount: s.amount, currency: s.currency, payee: s.payeeName };
}

function useScheduleActions() {
  const { toast } = useToast();
  const { fmt } = useHousehold();
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast(ok);
    } catch (e) {
      toast(errorMessage(e));
    }
    invalidateScopes(['transactions', 'accounts', 'plan']);
  };
  return {
    enter: (s: ScheduledDto) =>
      run(
        () => post(`/api/scheduled/${s.id}/enter`, {}),
        `Entered ${s.payeeName || 'transaction'} · unapproved in the register`,
      ),
    skip: (s: ScheduledDto) =>
      run(() => post(`/api/scheduled/${s.id}/skip`), `Skipped ${fmt.date(s.dateNext)}`),
    remove: (s: ScheduledDto) =>
      confirm(
        `Delete the scheduled series "${s.payeeName || 'transaction'}"? Transactions already entered stay.`,
      ) && run(() => del(`/api/scheduled/${s.id}`), 'Schedule deleted'),
  };
}

const canEditSeries = (s: ScheduledDto) => s.kind !== 'loan_interest';
const EDIT_INTEREST_HINT = 'Interest is computed from the loan; change its terms on the loan';

/** Approximate rendered height of the four-item menu, used to decide whether it fits below the button. */
const UP_MENU_HEIGHT = 150;

function UpcomingMenu({ s, onEdit }: { s: ScheduledDto; onEdit: () => void }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<CSSProperties>({});
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false), open);
  const act = useScheduleActions();
  // The list's rounded card clips its overflow, so the menu is positioned against the viewport instead.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);
  const toggle = (e: MouseEvent<HTMLButtonElement>) => {
    if (!open) {
      const r = e.currentTarget.getBoundingClientRect();
      const right = window.innerWidth - r.right;
      setPos(
        window.innerHeight - r.bottom < UP_MENU_HEIGHT + 12
          ? { right, bottom: window.innerHeight - r.top + 4 }
          : { right, top: r.bottom + 4 },
      );
    }
    setOpen(!open);
  };
  const item = (
    label: string,
    fn: () => void,
    o: { danger?: boolean; disabled?: boolean; title?: string } = {},
  ) => (
    <button
      role="menuitem"
      className={o.danger ? 'danger' : ''}
      disabled={o.disabled}
      title={o.title}
      onClick={() => {
        setOpen(false);
        fn();
      }}
    >
      {label}
    </button>
  );
  return (
    <div className="up-act" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        className="ib"
        aria-label={`Actions for ${s.payeeName || 'scheduled transaction'}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <Icon name="more" size={16} />
      </button>
      {open ? (
        <div className="up-menu" role="menu" style={pos}>
          {item('Enter now', () => void act.enter(s))}
          {item('Skip next', () => void act.skip(s))}
          {item('Edit series', onEdit, {
            disabled: !canEditSeries(s),
            title: canEditSeries(s) ? undefined : EDIT_INTEREST_HINT,
          })}
          {item('Delete series', () => void act.remove(s), { danger: true })}
        </div>
      ) : null}
    </div>
  );
}

function scheduleCategory(s: ScheduledDto) {
  if (s.kind === 'loan_interest') return <span className="muted">Loan interest</span>;
  if (s.splits.length) return <span className="split-tag">Split ({s.splits.length})</span>;
  if (s.transfer && !s.categoryName) return <span className="muted">Transfer</span>;
  return s.categoryName || <span className="warn-t">Needs a category</span>;
}

function UpcomingDesktop({
  account,
  onEdit,
}: {
  account: AccountDto | null;
  onEdit: (s: ScheduledDto) => void;
}) {
  const { fmt } = useHousehold();
  const list = useScheduled(account?.id).data ?? [];
  const [open, setOpen] = useState(true);
  if (!list.length) return null;
  const money = (v: number, currency: Currency, estimate: boolean) =>
    v ? (
      <span className="amtc">
        {estimate ? '≈ ' : ''}
        {currency === 'EUR' ? fmt.money(v, 'EUR') : fmt.num(v, 2)}
      </span>
    ) : (
      <span />
    );
  return (
    <div className="rt up-list" aria-label="Upcoming scheduled transactions">
      <button className="up-h" onClick={() => setOpen(!open)} aria-expanded={open}>
        <Icon name="cal" size={15} />
        <b>Upcoming</b>
        <span className="muted">· {list.length}</span>
        <span className="sp" />
        <span className="muted up-note">Each occurrence arrives unapproved in the register</span>
        <Icon name={open ? 'chevD' : 'chevR'} size={15} />
      </button>
      {open
        ? list.map((s) => {
            const v = seenFrom(s, account?.id);
            const est = s.kind === 'loan_interest';
            return (
              <div
                key={s.id}
                className={`rt-row up ${canEditSeries(s) ? 'click' : ''}`}
                onClick={() => canEditSeries(s) && onEdit(s)}
              >
                <span className="up-ic">
                  <Icon name="cal" size={14} />
                </span>
                <span>{s.flag ? <span className={`dot flag-${s.flag}`} /> : null}</span>
                <span>{fmt.date(s.dateNext)}</span>
                <span className="pay">
                  {s.transfer ? <Icon name="swap" size={14} /> : null}
                  {v.payee || <span className="muted">—</span>}
                </span>
                <span className="cat-c">
                  {!account ? <span className="muted">{s.accountName} · </span> : null}
                  {scheduleCategory(s)}
                </span>
                <span className="memo">
                  <span className="freq-tag">{FREQUENCY_LABELS[s.frequency]}</span>
                  {s.memo}
                </span>
                {money(v.amount < 0 ? -v.amount : 0, v.currency, est)}
                {money(v.amount > 0 ? v.amount : 0, v.currency, est)}
                <UpcomingMenu s={s} onEdit={() => onEdit(s)} />
              </div>
            );
          })
        : null}
    </div>
  );
}

function UpcomingMobile({
  account,
  onEdit,
}: {
  account: AccountDto | null;
  onEdit: (s: ScheduledDto) => void;
}) {
  const { fmt } = useHousehold();
  const list = useScheduled(account?.id).data ?? [];
  const [sel, setSel] = useState<ScheduledDto | null>(null);
  const act = useScheduleActions();
  if (!list.length) return null;
  return (
    <div className="up-m" aria-label="Upcoming scheduled transactions">
      <div className="dayh">
        <span>
          <Icon name="cal" size={13} /> Upcoming
        </span>
        <span>{list.length}</span>
      </div>
      {list.map((s) => {
        const v = seenFrom(s, account?.id);
        return (
          <div key={s.id} className="m-tx up" role="button" onClick={() => setSel(s)}>
            <span className="up-ic lg">
              <Icon name="cal" size={16} />
            </span>
            <div className="p">
              <b>{v.payee || '—'}</b>
              <small>
                {fmt.date(s.dateNext)} · {FREQUENCY_LABELS[s.frequency]}
                {s.categoryName ? ` · ${s.categoryName}` : ''}
              </small>
            </div>
            <div className={`a ${v.amount > 0 ? 'in' : ''}`}>
              {s.kind === 'loan_interest' ? '≈ ' : ''}
              {v.currency === 'EUR' ? fmt.money(v.amount, 'EUR') : fmt.num(v.amount, 2)}
            </div>
          </div>
        );
      })}
      {sel ? (
        <Sheet onClose={() => setSel(null)} label="Scheduled transaction">
          <div className="sh-h">
            <b>{seenFrom(sel, account?.id).payee || 'Scheduled transaction'}</b>
            <span className="muted" style={{ fontSize: 12 }}>
              Next {fmt.date(sel.dateNext)} · {FREQUENCY_LABELS[sel.frequency]}
            </span>
          </div>
          <div className="up-sheet">
            {(
              [
                ['Enter now', () => act.enter(sel), false, false],
                ['Skip next', () => act.skip(sel), false, false],
                ['Edit series', () => onEdit(sel), false, !canEditSeries(sel)],
                ['Delete series', () => act.remove(sel), true, false],
              ] as [string, () => unknown, boolean, boolean][]
            ).map(([label, fn, danger, disabled]) => (
              <button
                key={label}
                className={`btn ${danger ? 'ghost danger' : ''}`}
                disabled={disabled}
                title={disabled ? EDIT_INTEREST_HINT : undefined}
                onClick={() => {
                  setSel(null);
                  void fn();
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </Sheet>
      ) : null}
    </div>
  );
}

function Balances({ a, fmt }: { a: AccountDto; fmt: Fmt }) {
  const bal = (label: string, v: number, base: number, cls = '') => (
    <div className={cls}>
      <small>{label}</small>
      <b>{fmt.money(v, a.currency, 2)}</b>
      {a.currency !== fmt.base ? (
        <span className="amt2">
          ≈ {fmt.num(base)} {fmt.base}
        </span>
      ) : null}
    </div>
  );
  return (
    <div className="bals">
      {bal('Cleared', a.cleared, a.clearedBase)}
      {bal('Uncleared', a.uncleared, a.unclearedBase)}
      {bal('Working balance', a.working, a.workingBase, a.working < 0 ? 'work neg' : 'work')}
    </div>
  );
}

/** All accounts: the budget accounts' balances, in base currency (like the sidebar's Budget total). */
function AllBalances({ accounts, fmt }: { accounts: AccountDto[]; fmt: Fmt }) {
  const budget = accounts.filter((a) => !a.closed && a.group === 'budget');
  const sum = (f: (a: AccountDto) => number) => budget.reduce((s, a) => s + f(a), 0);
  const working = sum((a) => a.workingBase);
  const bal = (label: string, v: number, cls = '') => (
    <div className={cls}>
      <small>{label}</small>
      <b>{fmt.money(v, fmt.base, 2)}</b>
    </div>
  );
  return (
    <div className="bals" title="Budget accounts, converted to the base currency">
      {bal(
        'Cleared',
        sum((a) => a.clearedBase),
      )}
      {bal(
        'Uncleared',
        sum((a) => a.unclearedBase),
      )}
      {bal('Working balance', working, working < 0 ? 'work neg' : 'work')}
    </div>
  );
}

function ClearedBadge({ t, onToggle }: { t: TransactionDto; onToggle: () => void }) {
  if (t.cleared === 'reconciled')
    return (
      <span className="clr r" title="Reconciled">
        <Icon name="lock" size={14} />
      </span>
    );
  return (
    <button
      className={`clr ${t.cleared === 'cleared' ? 'c' : 'u'}`}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      title={
        t.cleared === 'cleared' ? 'Cleared — click to mark uncleared' : 'Uncleared — click to mark cleared'
      }
      aria-label={t.cleared === 'cleared' ? 'Cleared' : 'Uncleared'}
    >
      C
    </button>
  );
}

function useRegisterData(account: AccountDto | null, category?: string, month?: string) {
  const { today, household } = useHousehold();
  const st = useRegisterState(account?.id ?? 'all', {
    tokens: category ? [{ field: 'category', id: category, value: '' }] : undefined,
    range: month ? { from: month, to: month } : undefined,
  });
  const txs = useTransactions({ accountId: account?.id });
  const accounts = useAccounts();
  const all = useMemo(() => txs.data ?? [], [txs.data]);
  /** Tracking accounts (assets, loans) never take a category, so a missing one isn't a problem there. */
  const trackingIds = useMemo(
    () => new Set((accounts.data ?? []).filter((a) => !a.onBudget).map((a) => a.id)),
    [accounts.data],
  );
  const tracking = useCallback((accountId: string) => trackingIds.has(accountId), [trackingIds]);
  const q = useDebounced(st.text, 120);
  /** Text not yet picked from the suggestions still filters, as if it were a token. */
  const tokens = useMemo(() => {
    const typed = parseTyped(q);
    return typed ? [...st.tokens, typed] : st.tokens;
  }, [q, st.tokens]);
  const visible = useMemo(
    () => all.filter((t) => inView(t, st.view, today) && matchesSearch(t, tokens, { tracking })),
    [all, st.view, today, tokens, tracking],
  );
  const list = useMemo(() => sortTxs(visible, st.sort, !account), [visible, st.sort, account]);
  const source = useMemo<SuggestSource>(() => {
    const cats = new Map<string, { id: string; name: string; n: number }>();
    const payees = new Map<string, number>();
    const addCat = (id: string | null, name: string) => {
      if (!id || !name) return;
      const c = cats.get(id) ?? { id, name, n: 0 };
      c.n++;
      cats.set(id, c);
    };
    for (const t of all) {
      addCat(t.categoryId, t.categoryName);
      for (const sp of t.splits) addCat(sp.categoryId, sp.categoryName);
      if (t.payeeId && t.payeeName) payees.set(t.payeeName, (payees.get(t.payeeName) ?? 0) + 1);
    }
    return {
      accounts: account
        ? undefined
        : [...(accounts.data ?? [])].sort((a, b) => Number(a.closed) - Number(b.closed)),
      categories: [...cats.values()].sort((a, b) => b.n - a.n),
      payees: [...payees.entries()].sort((a, b) => b[1] - a[1]).map(([p]) => p),
      members: household.members,
    };
  }, [all, account, accounts.data, household.members]);
  /** First and last month with transactions (and always this month), for the View date pickers. */
  const span = useMemo(() => {
    const m = monthOf(today);
    const first = all.length ? monthOf(all[all.length - 1]!.date) : m;
    const last = all.length ? monthOf(all[0]!.date) : m;
    return { from: first < m ? first : m, to: last > m ? last : m };
  }, [all, today]);
  const searching = tokens.length > 0;
  const narrowed =
    searching || !st.view.showReconciled || !('preset' in st.view.range) || st.view.range.preset !== 'all';
  return {
    ...st,
    txs,
    all,
    list,
    tracking,
    source,
    span,
    searching,
    narrowed,
    accounts: accounts.data ?? [],
  };
}

type Col = {
  key: string;
  w: string;
  sort?: SortKey;
  label?: ReactNode;
  aria?: string;
  r?: boolean;
};

/** Column set for the desktop table: All accounts adds Account; View can hide Flags and Memo. */
function columns(all: boolean, view: RegisterView): Col[] {
  return [
    { key: 'cb', w: '30px' },
    ...(view.showFlags
      ? [
          {
            key: 'flag',
            w: '22px',
            sort: 'flag' as const,
            label: <Icon name="flag" size={13} />,
            aria: 'flag',
          },
        ]
      : []),
    { key: 'date', w: '86px', sort: 'date', label: 'Date' },
    { key: 'payee', w: all ? '1.3fr' : '1.1fr', sort: 'payee', label: 'Payee' },
    ...(all ? [{ key: 'account', w: '0.85fr', sort: 'account' as const, label: 'Account' }] : []),
    { key: 'category', w: all ? '1.15fr' : '1.55fr', sort: 'category', label: 'Category' },
    ...(view.showMemo
      ? [{ key: 'memo', w: all ? '0.85fr' : '1fr', sort: 'memo' as const, label: 'Memo' }]
      : []),
    { key: 'outflow', w: '116px', sort: 'outflow', label: 'Outflow', r: true },
    { key: 'inflow', w: '116px', sort: 'inflow', label: 'Inflow', r: true },
    {
      key: 'cleared',
      w: '40px',
      sort: 'cleared',
      label: <Icon name="check" size={14} />,
      aria: 'cleared status',
      r: true,
    },
  ];
}

function SortHeader({ c, sort, onSort }: { c: Col; sort: Sort; onSort: (s: Sort) => void }) {
  if (!c.sort) return <span />;
  const on = sort.key === c.sort;
  const name = c.aria ?? String(c.label);
  return (
    <span className={c.r ? 'r' : ''}>
      <button
        type="button"
        className={`sort-h ${on ? 'on' : ''}`}
        aria-label={`Sort by ${name}${on ? `, ${sort.dir === 'asc' ? 'ascending' : 'descending'}` : ''}`}
        title={`Sort by ${name}`}
        onClick={() =>
          onSort({
            key: c.sort!,
            dir: on ? (sort.dir === 'asc' ? 'desc' : 'asc') : firstDir(c.sort!),
          })
        }
      >
        {c.label}
        {on ? (
          <Icon name="chevD" size={12} stroke={2.2} className={sort.dir === 'asc' ? 'flip' : ''} />
        ) : null}
      </button>
    </span>
  );
}

function RegisterDesktop({
  account,
  category,
  month,
}: {
  account: AccountDto | null;
  category?: string;
  month?: string;
}) {
  const { fmt, today } = useHousehold();
  const count = (n: number) => fmt.num(n * 100);
  const { toast } = useToast();
  const { addTransaction } = useShell();
  const reg = useRegisterData(account, category, month);
  const { txs, list, tracking, view, sort } = reg;
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [series, setSeries] = useState<ScheduledDto | null>(null);
  /** Bulk actions only touch selected rows the current search still shows. */
  const selected = useMemo(() => list.filter((t) => checked.has(t.id)).map((t) => t.id), [list, checked]);
  const unapproved = account
    ? account.unapprovedCount
    : reg.accounts.reduce((s, a) => s + a.unapprovedCount, 0);
  const unapOn = reg.tokens.some((k) => k.field === 'is' && k.id === 'unapproved');
  const showBase = (t: TransactionDto) => t.currency !== fmt.base;
  const cols = columns(!account, view);
  const has = (key: string) => cols.some((c) => c.key === key);
  const grid = { '--rt-cols': cols.map((c) => c.w).join(' ') } as CSSProperties;
  const thisYear = today.slice(0, 4);
  const date = (d: string) => (d.startsWith(thisYear) ? fmt.date(d) : formatDateLong(d, fmt.locale));

  const bulk = async (fn: (id: string) => Promise<unknown>, label: string) => {
    try {
      for (const id of selected) await fn(id);
      toast(`${label} ${selected.length} transaction${selected.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast(errorMessage(e));
    }
    setChecked(new Set());
    invalidateScopes(['transactions', 'plan', 'accounts', 'activity']);
  };
  const toggleCleared = async (t: TransactionDto) => {
    try {
      await patch(`/api/transactions/${t.id}`, {
        cleared: t.cleared === 'cleared' ? 'uncleared' : 'cleared',
      });
    } catch (e) {
      toast(errorMessage(e));
    }
    invalidateScopes(['transactions', 'accounts']);
  };
  const clearAll = () => {
    reg.setTokens([]);
    reg.setText('');
  };

  const money = (v: number, currency: Currency) => (currency === 'EUR' ? fmt.money(v, 'EUR') : fmt.num(v, 2));
  const amt = (v: number, t: TransactionDto, cls = '') =>
    v ? (
      <span className={`amtc ${cls}`}>
        {money(v, t.currency)}
        {showBase(t) ? (
          <span className="amt2">
            {fmt.num(Math.abs(t.baseAmount))} {fmt.base}
          </span>
        ) : null}
      </span>
    ) : (
      <span />
    );
  /** Totals of what the search shows: native in one account, base currency across accounts. */
  const totals = useMemo(() => {
    let out = 0;
    let inn = 0;
    for (const t of list) {
      const v = account ? t.amount : t.baseAmount;
      if (v < 0) out -= v;
      else inn += v;
    }
    return { out, inn };
  }, [list, account]);
  const totalCurrency = account?.currency ?? fmt.base;

  return (
    <>
      <div className="reg-h">
        <div>
          <h2>
            {account ? account.name : 'All accounts'}{' '}
            {account ? <CurrencyChip currency={account.currency} /> : null}
          </h2>
          <div className="bank">
            {account
              ? [
                  account.institution,
                  ...account.cards.map((c) => `${c.label || 'Card'} •• ${c.last4}`),
                  `${KIND_LABEL[account.kind]} · ${account.onBudget ? 'budget' : 'tracking'}`,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : 'Every transaction across your accounts'}
          </div>
        </div>
        {account ? <Balances a={account} fmt={fmt} /> : <AllBalances accounts={reg.accounts} fmt={fmt} />}
      </div>
      {account?.kind === 'loan' ? <LoanPanel account={account} /> : null}
      <div className="reg-tools">
        <Btn variant="primary" icon="plus" onClick={() => addTransaction({ accountId: account?.id })}>
          Add transaction
        </Btn>
        {account ? (
          <>
            <Btn icon="upload" onClick={() => setDialog('import')}>
              Import CSV
            </Btn>
            <Btn icon="lock" onClick={() => setDialog('reconcile')}>
              Reconcile
            </Btn>
            {!account.closed ? (
              <Btn icon="swap" onClick={() => setDialog('switch')}>
                Switch to {otherCurrency(account.currency)}
              </Btn>
            ) : null}
            <Btn icon="edit" onClick={() => setDialog('edit')}>
              Edit account
            </Btn>
          </>
        ) : null}
        {selected.length ? (
          <>
            <span className="muted" style={{ fontSize: 13, marginLeft: 8 }}>
              {selected.length} selected
            </span>
            <Btn
              onClick={() => bulk((id) => patch(`/api/transactions/${id}`, { approved: true }), 'Approved')}
            >
              Approve
            </Btn>
            <Btn
              onClick={() =>
                bulk((id) => patch(`/api/transactions/${id}`, { cleared: 'cleared' }), 'Cleared')
              }
            >
              Mark cleared
            </Btn>
            <Btn
              variant="danger"
              onClick={() =>
                confirm(`Delete ${selected.length} transaction(s)?`) &&
                bulk((id) => del(`/api/transactions/${id}`), 'Deleted')
              }
            >
              Delete
            </Btn>
          </>
        ) : null}
        <span style={{ flex: 1 }} />
        {unapproved || unapOn ? (
          <button
            type="button"
            className={`chip ${unapOn ? 'on' : ''}`}
            aria-pressed={unapOn}
            onClick={() =>
              reg.setTokens(
                unapOn
                  ? reg.tokens.filter((k) => !(k.field === 'is' && k.id === 'unapproved'))
                  : [...reg.tokens, { field: 'is', id: 'unapproved', value: 'Unapproved' }],
              )
            }
          >
            Unapproved <span className="ct">{unapproved}</span>
          </button>
        ) : null}
        <ViewOptions view={view} onChange={reg.setView} today={today} span={reg.span} />
        <RegisterSearch
          tokens={reg.tokens}
          onTokens={reg.setTokens}
          text={reg.text}
          onText={reg.setText}
          source={reg.source}
          shortcut
        />
      </div>
      {reg.narrowed && !txs.isPending ? (
        <div className="reg-sum" role="status">
          <span>
            <b>{count(list.length)}</b> of {count(reg.all.length)} transactions
            {rangeLabel(view, today) ? ` · ${rangeLabel(view, today)}` : ''}
          </span>
          <span className="muted">
            Outflow {fmt.money(totals.out, totalCurrency, 2)} · Inflow{' '}
            {fmt.money(totals.inn, totalCurrency, 2)} · Net{' '}
            <b>{fmt.money(totals.inn - totals.out, totalCurrency, 2)}</b>
          </span>
          {reg.searching ? (
            <button className="link" onClick={clearAll}>
              Clear search
            </button>
          ) : null}
        </div>
      ) : null}
      {view.showUpcoming && !reg.searching ? <UpcomingDesktop account={account} onEdit={setSeries} /> : null}
      <div className="rt rt-main" aria-label="Transactions" style={grid}>
        <div className="rt-row th">
          <span>
            <input
              type="checkbox"
              className="cb"
              aria-label="Select all"
              checked={!!list.length && selected.length === list.length}
              onChange={(e) => setChecked(e.target.checked ? new Set(list.map((t) => t.id)) : new Set())}
            />
          </span>
          {cols.slice(1).map((c) => (
            <SortHeader key={c.key} c={c} sort={sort} onSort={reg.setSort} />
          ))}
        </div>
        {txs.isPending ? <Spinner /> : null}
        {!txs.isPending && !list.length ? (
          <div className="empty-reg">
            {reg.narrowed ? (
              <>
                No transactions match.{' '}
                {reg.searching ? (
                  <button className="link" onClick={clearAll}>
                    Clear search
                  </button>
                ) : null}
              </>
            ) : (
              'No transactions yet. Add one, or import a CSV from your bank.'
            )}
          </div>
        ) : null}
        {list.map((t) => (
          <div key={t.id} style={{ display: 'contents' }}>
            <div
              className={`rt-row click ${t.approved ? '' : 'unap'}`}
              onClick={() => addTransaction({ tx: t })}
            >
              <span onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  className="cb"
                  aria-label={`Select ${t.payeeName}`}
                  checked={checked.has(t.id)}
                  onChange={(e) =>
                    setChecked((s) => {
                      const n = new Set(s);
                      if (e.target.checked) n.add(t.id);
                      else n.delete(t.id);
                      return n;
                    })
                  }
                />
              </span>
              {has('flag') ? <span>{t.flag ? <span className={`dot flag-${t.flag}`} /> : null}</span> : null}
              <span>{date(t.date)}</span>
              <span className="pay">
                {t.transfer ? <Icon name="swap" size={14} /> : null}
                {t.payeeName || <span className="muted">—</span>}
                {t.scheduledId ? (
                  <span className="sched-ic" title="Entered from a schedule">
                    <Icon name="cal" size={13} />
                  </span>
                ) : null}
              </span>
              {has('account') ? <span className="acc-c">{t.accountName}</span> : null}
              <span className="cat-c">
                {t.transfer && t.transfer.otherCurrency !== t.currency ? (
                  <span className="xfer">
                    {fmt.money(t.amount, t.currency, 2, 'auto')} ·{' '}
                    {fmt.money(t.transfer.otherAmount, t.transfer.otherCurrency, 0, 'always')} @{' '}
                    {fmt.rate(
                      t.currency === 'EUR' ? t.transfer.impliedRate : invertRate(t.transfer.impliedRate),
                    )}
                  </span>
                ) : t.splits.length ? (
                  <span className="split-tag">Split ({t.splits.length})</span>
                ) : t.transfer ? (
                  <span className="muted">Transfer</span>
                ) : t.categoryName ? (
                  t.categoryName
                ) : tracking(t.accountId) ? (
                  <span className="muted">—</span>
                ) : (
                  <span className="warn-t">Needs a category</span>
                )}
              </span>
              {has('memo') ? (
                <span className="memo">
                  <Avatar id={t.createdBy} />
                  {t.memo}
                </span>
              ) : null}
              {amt(t.amount < 0 ? -t.amount : 0, t)}
              {amt(t.amount > 0 ? t.amount : 0, t, 'inflow')}
              <ClearedBadge t={t} onToggle={() => toggleCleared(t)} />
            </div>
            {t.splits.map((sp) => (
              <div className="rt-row sub" key={sp.id}>
                {cols
                  .slice(
                    0,
                    cols.findIndex((c) => c.key === 'category'),
                  )
                  .map((c) => (
                    <span key={c.key} />
                  ))}
                <span className="cat-c">
                  {sp.categoryName || <span className="warn-t">Needs a category</span>}
                </span>
                {has('memo') ? <span className="memo">{sp.memo}</span> : null}
                <span className="amtc">{sp.amount < 0 ? money(-sp.amount, t.currency) : ''}</span>
                <span className="amtc inflow">{sp.amount > 0 ? money(sp.amount, t.currency) : ''}</span>
                <span />
              </div>
            ))}
          </div>
        ))}
      </div>
      {dialog === 'reconcile' && account ? (
        <ReconcileDialog account={account} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'import' && account ? (
        <Suspense fallback={null}>
          <ImportDialog account={account} onClose={() => setDialog(null)} />
        </Suspense>
      ) : null}
      {dialog === 'switch' && account ? (
        <SwitchCurrencyDialog account={account} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'edit' && account ? (
        <EditAccountDialog account={account} onClose={() => setDialog(null)} />
      ) : null}
      {series ? <TransactionDialog schedule={series} onClose={() => setSeries(null)} /> : null}
    </>
  );
}

function RegisterMobile({
  account,
  category,
  month,
}: {
  account: AccountDto | null;
  category?: string;
  month?: string;
}) {
  const { fmt, today, household } = useHousehold();
  const count = (n: number) => fmt.num(n * 100);
  const navigate = useNavigate();
  const { addTransaction } = useShell();
  const reg = useRegisterData(account, category, month);
  const { txs, tracking, view } = reg;
  const [who, setWho] = useState<string>('all');
  const [searching, setSearching] = useState(reg.searching);
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [series, setSeries] = useState<ScheduledDto | null>(null);
  const list = useMemo(() => reg.list.filter((t) => who === 'all' || t.createdBy === who), [reg.list, who]);
  const days = useMemo(() => {
    const out: { day: string; items: TransactionDto[] }[] = [];
    for (const t of list) {
      const last = out[out.length - 1];
      if (last && last.day === t.date) last.items.push(t);
      else out.push({ day: t.date, items: [t] });
    }
    return out;
  }, [list]);
  return (
    <>
      <div className="m-h">
        <button className="ib" onClick={() => navigate('/accounts')} aria-label="Back to accounts">
          <Icon name="chevL" />
        </button>
        <h1 style={{ fontSize: 22 }}>{account?.name ?? 'All accounts'}</h1>
        <ViewOptions view={view} onChange={reg.setView} today={today} span={reg.span} mobile />
        <button
          className={`ib ${reg.searching ? 'on' : ''}`}
          onClick={() => setSearching((s) => !s)}
          aria-label="Search"
          aria-expanded={searching}
        >
          <Icon name="search" />
        </button>
      </div>
      {searching ? (
        <div className="m-search">
          <RegisterSearch
            tokens={reg.tokens}
            onTokens={reg.setTokens}
            text={reg.text}
            onText={reg.setText}
            source={reg.source}
            autoFocus={!reg.searching}
          />
        </div>
      ) : null}
      {reg.narrowed && !txs.isPending ? (
        <div className="m-sum" role="status">
          {count(list.length)} of {count(reg.all.length)}
          {rangeLabel(view, today) ? ` · ${rangeLabel(view, today)}` : ''}
          {reg.searching ? (
            <button
              className="link"
              onClick={() => {
                reg.setTokens([]);
                reg.setText('');
              }}
            >
              Clear search
            </button>
          ) : null}
        </div>
      ) : null}
      {account ? (
        <div className="m-acct">
          <div className="t">
            <CurrencyChip currency={account.currency} />{' '}
            {[account.institution, ...account.cards.map((c) => `•• ${c.last4}`)]
              .filter(Boolean)
              .join(' · ') || KIND_LABEL[account.kind]}
          </div>
          <b>{fmt.money(account.working, account.currency, 2)}</b>
          <div className="bb">
            <span>
              Cleared <b>{fmt.num(account.cleared, 2)}</b>
            </span>
            <span>
              Uncleared <b>{fmt.num(account.uncleared, 2)}</b>
            </span>
            {account.currency !== fmt.base ? (
              <span>
                ≈ {fmt.num(account.workingBase)} {fmt.base}
              </span>
            ) : null}
          </div>
          <div className="row-gap" style={{ marginTop: 10 }}>
            <button className="chip" onClick={() => addTransaction({ accountId: account.id })}>
              <Icon name="plus" size={14} />
              Add
            </button>
            <button className="chip" onClick={() => setDialog('reconcile')}>
              <Icon name="lock" size={14} />
              Reconcile
            </button>
            <button className="chip" onClick={() => setDialog('import')}>
              <Icon name="upload" size={14} />
              Import
            </button>
            {!account.closed ? (
              <button className="chip" onClick={() => setDialog('switch')}>
                <Icon name="swap" size={14} />
                To {otherCurrency(account.currency)}
              </button>
            ) : null}
            <button className="chip" onClick={() => setDialog('edit')}>
              <Icon name="edit" size={14} />
              Edit
            </button>
          </div>
        </div>
      ) : null}
      {account?.kind === 'loan' ? <LoanPanel account={account} /> : null}
      <div style={{ padding: '2px 12px 0' }}>
        <Seg
          full
          value={who}
          onChange={setWho}
          label="Who added it"
          options={[
            ['all', 'Both'],
            ...household.members.map(
              (m) =>
                [
                  m.id,
                  <>
                    <Avatar member={m} />
                    {m.displayName}
                  </>,
                ] as [string, React.ReactNode],
            ),
          ]}
        />
      </div>
      {view.showUpcoming && !reg.searching ? <UpcomingMobile account={account} onEdit={setSeries} /> : null}
      {txs.isPending ? <Spinner /> : null}
      {!txs.isPending && !list.length ? (
        <div className="empty-reg">{reg.narrowed ? 'No transactions match.' : 'No transactions.'}</div>
      ) : null}
      {days.map((d) => (
        <div key={d.day}>
          <div className="dayh">
            <span>{dayLabel(d.day, today, fmt.locale)}</span>
            <span>
              {fmt.num(
                d.items.reduce((s, t) => s + (account ? t.amount : t.baseAmount), 0),
                2,
              )}
            </span>
          </div>
          {d.items.map((t) => (
            <MobileTxRow
              key={t.id}
              t={t}
              fmt={fmt}
              tracking={tracking(t.accountId)}
              onClick={() => addTransaction({ tx: t })}
            />
          ))}
        </div>
      ))}
      <div style={{ height: 20 }} />
      {dialog === 'reconcile' && account ? (
        <ReconcileDialog account={account} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'import' && account ? (
        <Suspense fallback={null}>
          <ImportDialog account={account} onClose={() => setDialog(null)} />
        </Suspense>
      ) : null}
      {dialog === 'switch' && account ? (
        <SwitchCurrencyDialog account={account} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'edit' && account ? (
        <EditAccountDialog account={account} onClose={() => setDialog(null)} />
      ) : null}
      {series ? <TransactionDialog schedule={series} onClose={() => setSeries(null)} /> : null}
    </>
  );
}

export function MobileTxRow({
  t,
  fmt,
  onClick,
  tracking,
}: {
  t: TransactionDto;
  fmt: Fmt;
  onClick?: () => void;
  tracking?: boolean;
}) {
  const sub = t.transfer
    ? `Transfer · ${fmt.money(t.transfer.otherAmount, t.transfer.otherCurrency, 2, 'always')}`
    : t.splits.length
      ? `Split (${t.splits.length})`
      : t.categoryName || (tracking ? '' : 'Needs a category');
  return (
    <div
      className="m-tx"
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      style={
        !t.approved ? { background: 'color-mix(in srgb, var(--brand-soft) 70%, var(--surface))' } : undefined
      }
    >
      <Avatar id={t.createdBy} lg />
      <div className="p">
        <b>{t.payeeName || '—'}</b>
        <small>
          {sub}
          {t.memo ? ` · ${t.memo}` : ''}
          {!t.approved ? ' · Unapproved' : ''}
        </small>
      </div>
      <div className={`a ${t.amount > 0 ? 'in' : ''}`}>
        {t.currency === 'EUR' ? fmt.money(t.amount, 'EUR') : fmt.num(t.amount, 2)}
        {t.currency !== fmt.base ? (
          <span className="amt2">
            ≈ {fmt.num(t.baseAmount)} {fmt.base}
          </span>
        ) : null}
      </div>
    </div>
  );
}
